import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import React, { useEffect, useState } from 'react';
import {
  withInventoryCalculationParametersHoc,
  WithInventoryCalculationParametersHocChildProps,
} from '../../../api/graphql/hoc/withInventoryCalculationParametersHoc';
import {
  withUpdateInventoryCalculationParametersHoc,
  WithUpdateInventoryCalculationParametersHocChildProps,
} from '../../../api/graphql/hoc/withUpdateInventoryCalculationParametersHoc';
import { DialogSpinner } from '../../spinner';

const useStyles = makeStyles({
  dialogTitle: {
    fontWeight: 700,
    textAlign: 'center',
  },
  sectionHeader: {
    fontWeight: 600,
    marginTop: 8,
    marginBottom: 4,
    textAlign: 'center',
  },
  section: {
    marginTop: 6,
    marginBottom: 6,
  },
  divider: {
    marginTop: 8,
    marginBottom: 12,
  },
  formControl: {
    width: '100%',
  },
  infoPaper: {
    padding: 8,
    background: '#F7F7F9',
  },
  dialogActions: {
    justifyContent: 'flex-end',
    paddingRight: 24,
    paddingBottom: 16,
  },
  saveButton: {
    backgroundColor: '#5C616A',
    color: '#FFFFFF',
    '&:hover': {
      backgroundColor: '#4A4E56',
    },
  },
  cancelButton: {
    border: '1px solid #D1D5DB',
    color: '#374151',
    marginRight: 8,
  },
});

type VolumeConversionFieldsProps = {
  volToBuOption: string;
  setVolToBuOption: (value: string) => void;
  buPerCuft: number;
  setBuPerCuft: (value: number) => void;
  error?: string;
  classes: Record<string, string>;
};

const VolumeConversionFields: React.FC<VolumeConversionFieldsProps> = ({
  volToBuOption,
  setVolToBuOption,
  buPerCuft,
  setBuPerCuft,
  error,
  classes,
}) => {
  return (
    <>
      <Grid item xs={10}>
        <FormControl variant="outlined" margin="dense" className={classes.formControl}>
          <InputLabel>Conversion Option</InputLabel>
          <Select
            value={volToBuOption}
            onChange={(e: any) => setVolToBuOption(e.target.value)}
            label="Conversion Option"
          >
            <MenuItem value="standard">Standard (0.80356395 bu. per cu.ft.)</MenuItem>
            <MenuItem value="custom">Custom bu. per cu.ft.</MenuItem>
          </Select>
        </FormControl>
        {volToBuOption === 'custom' && (
          <TextField
            fullWidth
            variant="outlined"
            margin="dense"
            type="number"
            error={!!error}
            helperText={error}
            value={buPerCuft}
            onChange={(e) =>
              setBuPerCuft(
                Number.isNaN(parseFloat(e.target.value)) ? 0 : parseFloat(e.target.value)
              )
            }
          />
        )}
      </Grid>
    </>
  );
};

type ConeVolumeFieldsProps = {
  coneOption: string;
  setConeOption: (value: string) => void;
  coneAdjHeight: number;
  setConeAdjHeight: (value: number) => void;
  error?: string;
  classes: Record<string, string>;
};

const ConeVolumeFields: React.FC<ConeVolumeFieldsProps> = ({
  coneOption,
  setConeOption,
  coneAdjHeight,
  setConeAdjHeight,
  error,
  classes,
}) => {
  return (
    <>
      <Grid item xs={10}>
        <FormControl variant="outlined" margin="dense" className={classes.formControl}>
          <InputLabel>Calculation Option</InputLabel>
          <Select
            value={coneOption}
            onChange={(e: any) => setConeOption(e.target.value)}
            label="Calculation Option"
          >
            <MenuItem value="standard">Standard</MenuItem>
            <MenuItem value="custom">Custom Cone Up/Down Adjustment Height</MenuItem>
          </Select>
        </FormControl>
        {coneOption === 'custom' && (
          <TextField
            fullWidth
            variant="outlined"
            margin="dense"
            type="number"
            error={!!error}
            helperText={error}
            value={coneAdjHeight}
            onChange={(e) =>
              setConeAdjHeight(
                Number.isNaN(parseFloat(e.target.value)) ? 0 : parseFloat(e.target.value)
              )
            }
            InputProps={{ endAdornment: <InputAdornment position="end">ft.</InputAdornment> }}
          />
        )}
      </Grid>
    </>
  );
};

type VolumeAdjustmentFieldsProps = {
  volAdjOption: GrainVolumeAdjustmentOption;
  setVolAdjOption: (value: GrainVolumeAdjustmentOption) => void;
  volAdjValue: number;
  setVolAdjValue: (value: number) => void;
  volAdjType: string;
  setVolAdjType: (value: string) => void;
  error?: string;
  classes: Record<string, string>;
};

const VolumeAdjustmentFields: React.FC<VolumeAdjustmentFieldsProps> = ({
  volAdjOption,
  setVolAdjOption,
  volAdjValue,
  setVolAdjValue,
  volAdjType,
  setVolAdjType,
  error,
  classes,
}) => {
  const handleTypeChange = (value: string) => {
    setVolAdjType(value);
    if (value === 'none') {
      setVolAdjOption(GrainVolumeAdjustmentOption.NONE);
      setVolAdjValue(0);
    } else {
      setVolAdjOption(
        value === 'deduction'
          ? GrainVolumeAdjustmentOption.DEDUCTION
          : GrainVolumeAdjustmentOption.ADDITION
      );
      // Ensure the sign of volAdjValue matches the new type
      if (value === 'deduction' && volAdjValue > 0) {
        setVolAdjValue(-volAdjValue);
      } else if (value === 'addition' && volAdjValue < 0) {
        setVolAdjValue(Math.abs(volAdjValue));
      }
    }
  };

  return (
    <>
      <Grid item xs={10}>
        <FormControl variant="outlined" margin="dense" className={classes.formControl}>
          <InputLabel>Adjustment</InputLabel>
          <Select
            value={volAdjType}
            onChange={(e: any) => handleTypeChange(e.target.value)}
            label="Adjustment"
          >
            <MenuItem value="none">None</MenuItem>
            <MenuItem value="deduction">Deduction (Negative)</MenuItem>
            <MenuItem value="addition">Addition (Positive)</MenuItem>
          </Select>
        </FormControl>
        {volAdjType !== 'none' && (
          <TextField
            fullWidth
            variant="outlined"
            margin="dense"
            type="number"
            error={!!error}
            helperText={error}
            value={Math.abs(volAdjValue)}
            onChange={(e) => {
              const val = Number.isNaN(parseFloat(e.target.value))
                ? 0
                : Math.abs(parseFloat(e.target.value));
              setVolAdjValue(volAdjType === 'deduction' ? -val : val);
            }}
            InputProps={{ endAdornment: <InputAdornment position="end">cu.ft</InputAdornment> }}
          />
        )}
      </Grid>
    </>
  );
};

type GrainDensityFieldsProps = {
  densityOption: string;
  setDensityOption: (value: string) => void;
  grainDensity: number;
  setGrainDensity: (value: number) => void;
  error?: string;
  classes: Record<string, string>;
};

const GrainDensityFields: React.FC<GrainDensityFieldsProps> = ({
  densityOption,
  setDensityOption,
  grainDensity,
  setGrainDensity,
  error,
  classes,
}) => {
  return (
    <>
      <Grid item xs={10}>
        <FormControl variant="outlined" margin="dense" className={classes.formControl}>
          <InputLabel>Density Option</InputLabel>
          <Select
            value={densityOption}
            onChange={(e: any) => setDensityOption(e.target.value)}
            label="Density Option"
          >
            <MenuItem value="standard">Standard</MenuItem>
            <MenuItem value="custom">Custom Density</MenuItem>
          </Select>
        </FormControl>
        {densityOption === 'custom' && (
          <TextField
            fullWidth
            variant="outlined"
            margin="dense"
            type="number"
            error={!!error}
            helperText={error}
            value={grainDensity}
            onChange={(e) =>
              setGrainDensity(
                Number.isNaN(parseFloat(e.target.value)) ? 0 : parseFloat(e.target.value)
              )
            }
            InputProps={{
              endAdornment: <InputAdornment position="end">lbs/cu.ft.</InputAdornment>,
            }}
          />
        )}
      </Grid>
    </>
  );
};

type UsdaPackFactorFieldsProps = {
  pfTestWeight: number;
  setPfTestWeight: (value: number) => void;
  pfValue: number;
  coneOption: string;
  setConeOption: (value: string) => void;
  coneAdjHeight: number;
  setConeAdjHeight: (value: number) => void;
  volAdjOption: GrainVolumeAdjustmentOption;
  setVolAdjOption: (value: GrainVolumeAdjustmentOption) => void;
  volAdjValue: number;
  setVolAdjValue: (value: number) => void;
  volAdjType: string;
  setVolAdjType: (value: string) => void;
  densityOption: string;
  setDensityOption: (value: string) => void;
  grainDensity: number;
  setGrainDensity: (value: number) => void;
  coneError?: string;
  volumeError?: string;
  densityError?: string;
  classes: Record<string, string>;
};

const UsdaPackFactorFields: React.FC<UsdaPackFactorFieldsProps> = ({
  pfTestWeight,
  setPfTestWeight,
  pfValue,
  coneOption,
  setConeOption,
  coneAdjHeight,
  setConeAdjHeight,
  volAdjOption,
  setVolAdjOption,
  volAdjValue,
  setVolAdjValue,
  volAdjType,
  setVolAdjType,
  densityOption,
  setDensityOption,
  grainDensity,
  setGrainDensity,
  coneError,
  volumeError,
  densityError,
  classes,
}) => {
  return (
    <>
      <Grid item xs={12}>
        <TextField
          fullWidth
          variant="outlined"
          margin="dense"
          type="number"
          label="Nominal Test Weight"
          value={pfTestWeight}
          onChange={(e) =>
            setPfTestWeight(
              Number.isNaN(parseFloat(e.target.value)) ? 0 : parseFloat(e.target.value)
            )
          }
          InputProps={{
            endAdornment: <InputAdornment position="end">lbs/bu</InputAdornment>,
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          variant="outlined"
          margin="dense"
          type="number"
          label="Pack Factor"
          value={pfValue}
          disabled
          InputProps={{
            readOnly: true,
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <Paper elevation={0} className={classes.infoPaper}>
          <Typography variant="body2">
            Pack Factor is calculated from the table based on Nominal Test Weight
          </Typography>
        </Paper>
      </Grid>

      {/* Cone Volume Section */}
      <Typography variant="subtitle2" color="textSecondary" className={classes.sectionHeader}>
        Cone Volume Calculation
      </Typography>
      <Grid container spacing={2} className={classes.section} justify="center">
        <ConeVolumeFields
          coneOption={coneOption}
          setConeOption={setConeOption}
          coneAdjHeight={coneAdjHeight}
          setConeAdjHeight={setConeAdjHeight}
          error={coneError}
          classes={classes}
        />
        <Grid item xs={10}>
          <Divider className={classes.divider} />
        </Grid>
      </Grid>

      {/* Grain Volume Offset Section */}
      <Typography variant="subtitle2" color="textSecondary" className={classes.sectionHeader}>
        Grain Volume Offset
      </Typography>
      <Grid container spacing={2} className={classes.section} justify="center">
        <VolumeAdjustmentFields
          volAdjOption={volAdjOption}
          setVolAdjOption={setVolAdjOption}
          volAdjValue={volAdjValue}
          setVolAdjValue={setVolAdjValue}
          volAdjType={volAdjType}
          setVolAdjType={setVolAdjType}
          error={volumeError}
          classes={classes}
        />
        <Grid item xs={10}>
          <Divider className={classes.divider} />
        </Grid>
      </Grid>

      {/* Grain Density Section */}
      <Typography variant="subtitle2" color="textSecondary" className={classes.sectionHeader}>
        Grain Density Calculation
      </Typography>
      <Grid container spacing={2} className={classes.section} justify="center">
        <GrainDensityFields
          densityOption={densityOption}
          setDensityOption={setDensityOption}
          grainDensity={grainDensity}
          setGrainDensity={setGrainDensity}
          error={densityError}
          classes={classes}
        />
        <Grid item xs={10}>
          <Divider className={classes.divider} />
        </Grid>
      </Grid>
    </>
  );
};

type CustomPackFactorTableFieldsProps = {
  pfTestWeight: number;
  setPfTestWeight: (value: number) => void;
  pfValue: number;
  classes: Record<string, string>;
};

const CustomPackFactorTableFields: React.FC<CustomPackFactorTableFieldsProps> = ({
  pfTestWeight,
  setPfTestWeight,
  pfValue,
  classes,
}) => {
  return (
    <>
      <Grid item xs={12}>
        <TextField
          fullWidth
          variant="outlined"
          margin="dense"
          type="number"
          label="Nominal Test Weight"
          value={pfTestWeight}
          onChange={(e) =>
            setPfTestWeight(
              Number.isNaN(parseFloat(e.target.value)) ? 0 : parseFloat(e.target.value)
            )
          }
          InputProps={{
            endAdornment: <InputAdornment position="end">lbs/bu</InputAdornment>,
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          variant="outlined"
          margin="dense"
          type="number"
          label="Pack Factor"
          value={pfValue}
          disabled
          InputProps={{
            readOnly: true,
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <Paper elevation={0} className={classes.infoPaper}>
          <Typography variant="body2">
            Pack Factor is calculated from the table based on Nominal Test Weight
          </Typography>
        </Paper>
      </Grid>
    </>
  );
};

type CustomPackFactorWithAdjustmentFieldsProps = {
  pfTestWeight: number;
  setPfTestWeight: (value: number) => void;
  pfAdjIndex: number;
  setPfAdjIndex: (value: number) => void;
  pfValue: number;
  setPfValue: (value: number) => void;
};

const CustomPackFactorWithAdjustmentFields: React.FC<CustomPackFactorWithAdjustmentFieldsProps> = ({
  pfTestWeight,
  setPfTestWeight,
  pfAdjIndex,
  setPfAdjIndex,
  pfValue,
  setPfValue,
}) => {
  return (
    <>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          variant="outlined"
          margin="dense"
          type="number"
          label="Nominal Test Weight"
          value={pfTestWeight}
          onChange={(e) =>
            setPfTestWeight(
              Number.isNaN(parseFloat(e.target.value)) ? 0 : parseFloat(e.target.value)
            )
          }
          InputProps={{
            endAdornment: <InputAdornment position="end">lbs/bu</InputAdornment>,
          }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          variant="outlined"
          margin="dense"
          label="Adjustment Index"
          value={pfAdjIndex}
          onChange={(e) => setPfAdjIndex(parseInt(e.target.value, 10))}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          variant="outlined"
          margin="dense"
          type="number"
          label="Pack Factor"
          value={pfValue}
          onChange={(e) =>
            setPfValue(Number.isNaN(parseFloat(e.target.value)) ? 0 : parseFloat(e.target.value))
          }
        />
      </Grid>
    </>
  );
};

type CustomPackFactorSingleFieldsProps = {
  pfValue: number;
  setPfValue: (value: number) => void;
  densityOption: string;
  setDensityOption: (value: string) => void;
  grainDensity: number;
  setGrainDensity: (value: number) => void;
  volToBuOption: string;
  setVolToBuOption: (value: string) => void;
  buPerCuft: number;
  setBuPerCuft: (value: number) => void;
  coneOption: string;
  setConeOption: (value: string) => void;
  coneAdjHeight: number;
  setConeAdjHeight: (value: number) => void;
  volAdjOption: GrainVolumeAdjustmentOption;
  setVolAdjOption: (value: GrainVolumeAdjustmentOption) => void;
  volAdjValue: number;
  setVolAdjValue: (value: number) => void;
  volAdjType: string;
  setVolAdjType: (value: string) => void;
  coneError?: string;
  volumeError?: string;
  conversionError?: string;
  densityError?: string;
  classes: Record<string, string>;
};

/**
 * CustomPackFactorSingleFields renders the configuration form fields when using a single
 * custom pack factor value for grain inventory calculations.
 *
 * This component provides a simplified pack factor approach where the user specifies
 * a single pack factor value directly, rather than using tables or calculations based
 * on test weight. This mode is useful when facilities have determined a consistent
 * pack factor through their own testing or experience.
 *
 * The component displays five main calculation configuration sections:
 * 1. Pack Factor Value - Direct manual input of the pack factor
 * 2. Volume to Bushel Conversion - Controls how cubic feet are converted to bushels
 * 3. Cone Volume Calculation - Adjusts for grain pile cone shape
 * 4. Grain Volume Offset - Manual volume adjustments (Deductions/Additions)
 * 5. Grain Density Calculation - Determines grain weight per cubic foot
 *
 * Unlike NO_PACK_FACTOR mode, this method applies pack factor compensation using
 * the user-specified value, which accounts for grain compaction effects on inventory
 * weight calculations.
 *
 * **GraphQL Model Fields Updated (InventoryCalculationParameters):**
 *
 * When CUSTOM_PACK_FACTOR_SINGLE is selected, the following fields are expected to be set/updated:
 * - `pack_factor_calculation_option` - Set to 'CUSTOM_PACK_FACTOR_SINGLE'
 * - `pack_factor_value` - The manually specified pack factor value
 * - `volume_to_bu_conversion_option` - Either 'standard' or 'custom' based on volToBuOption
 * - `bu_per_cuft_conversion_factor` - Custom bu/cu.ft. value when volToBuOption is 'custom'
 * - `grain_volume_calculation_option` - Either 'standard' or 'custom' based on coneOption
 * - `equivalent_height_cone_adjustment` - Height adjustment in feet when coneOption is 'custom'
 * - `grain_volume_adjustment_option` - One of 'None', 'Deduction', 'Addition' based on volAdjOption
 * - `grain_volume_adjustment_cuft` - Volume adjustment in cu.ft. when volAdjOption is not 'None'
 * - `grain_density_calculation_option` - Either 'standard' or 'custom' based on densityOption
 * - `grain_density_lbspercuft` - Custom density in lbs/cu.ft. when densityOption is 'custom'
 *
 * @param props - Component properties including all calculation option states and setters
 * @param props.pfValue - Custom pack factor value to apply
 * @param props.setPfValue - Setter for pack factor value
 * @param props.densityOption - Grain density option: 'standard' or 'custom'
 * @param props.setDensityOption - Setter for density option
 * @param props.grainDensity - Custom grain density in lbs/cu.ft.
 * @param props.setGrainDensity - Setter for grain density value
 * @param props.volToBuOption - Volume conversion option: 'standard' or 'custom'
 * @param props.setVolToBuOption - Setter for volume conversion option
 * @param props.buPerCuft - Bushels per cubic foot conversion factor
 * @param props.setBuPerCuft - Setter for bu/cu.ft. conversion factor
 * @param props.coneOption - Cone calculation option: 'standard' or 'custom'
 * @param props.setConeOption - Setter for cone calculation option
 * @param props.coneAdjHeight - Cone adjustment height in feet
 * @param props.setConeAdjHeight - Setter for cone adjustment height
 * @param props.volAdjOption - Volume adjustment option: 'standard' or 'custom'
 * @param props.setVolAdjOption - Setter for volume adjustment option
 * @param props.volAdjValue - Volume adjustment value in cu.ft.
 * @param props.setVolAdjValue - Setter for volume adjustment value
 * @param props.volAdjType - Volume adjustment type: 'none', 'deduction', or 'addition'
 * @param props.setVolAdjType - Setter for volume adjustment type
 * @param props.coneError - Error message for cone adjustment validation
 * @param props.volumeError - Error message for volume adjustment validation
 * @param props.conversionError - Error message for volume conversion validation
 * @param props.densityError - Error message for grain density validation
 * @param props.classes - Material-UI style classes object
 */
const CustomPackFactorSingleFields: React.FC<CustomPackFactorSingleFieldsProps> = ({
  pfValue,
  setPfValue,
  densityOption,
  setDensityOption,
  grainDensity,
  setGrainDensity,
  volToBuOption,
  setVolToBuOption,
  buPerCuft,
  setBuPerCuft,
  coneOption,
  setConeOption,
  coneAdjHeight,
  setConeAdjHeight,
  volAdjOption,
  setVolAdjOption,
  volAdjValue,
  setVolAdjValue,
  volAdjType,
  setVolAdjType,
  coneError,
  volumeError,
  conversionError,
  densityError,
  classes,
}) => {
  return (
    <>
      <Grid item xs={10}>
        <TextField
          fullWidth
          variant="outlined"
          margin="dense"
          type="number"
          label="Pack Factor"
          value={pfValue}
          onChange={(e) =>
            setPfValue(Number.isNaN(parseFloat(e.target.value)) ? 0 : parseFloat(e.target.value))
          }
        />
      </Grid>
      <Grid item xs={10}>
        <Divider className={classes.divider} />
      </Grid>

      {/* Volume to Bu. Section */}
      <Typography variant="subtitle2" color="textSecondary" className={classes.sectionHeader}>
        Volume to Bu. Conversion
      </Typography>
      <Grid container spacing={2} className={classes.section} justify="center">
        <VolumeConversionFields
          volToBuOption={volToBuOption}
          setVolToBuOption={setVolToBuOption}
          buPerCuft={buPerCuft}
          setBuPerCuft={setBuPerCuft}
          error={conversionError}
          classes={classes}
        />
        <Grid item xs={10}>
          <Divider className={classes.divider} />
        </Grid>
      </Grid>

      {/* Cone Volume Section */}
      <Typography variant="subtitle2" color="textSecondary" className={classes.sectionHeader}>
        Cone Volume Calculation
      </Typography>
      <Grid container spacing={2} className={classes.section} justify="center">
        <ConeVolumeFields
          coneOption={coneOption}
          setConeOption={setConeOption}
          coneAdjHeight={coneAdjHeight}
          setConeAdjHeight={setConeAdjHeight}
          error={coneError}
          classes={classes}
        />
        <Grid item xs={10}>
          <Divider className={classes.divider} />
        </Grid>
      </Grid>

      {/* Grain Volume Offset Section */}
      <Typography variant="subtitle2" color="textSecondary" className={classes.sectionHeader}>
        Grain Volume Offset
      </Typography>
      <Grid container spacing={2} className={classes.section} justify="center">
        <VolumeAdjustmentFields
          volAdjOption={volAdjOption}
          setVolAdjOption={setVolAdjOption}
          volAdjValue={volAdjValue}
          setVolAdjValue={setVolAdjValue}
          volAdjType={volAdjType}
          setVolAdjType={setVolAdjType}
          error={volumeError}
          classes={classes}
        />
        <Grid item xs={10}>
          <Divider className={classes.divider} />
        </Grid>
      </Grid>

      {/* Grain Density Section */}
      <Typography variant="subtitle2" color="textSecondary" className={classes.sectionHeader}>
        Grain Density Calculation
      </Typography>
      <Grid container spacing={2} className={classes.section} justify="center">
        <GrainDensityFields
          densityOption={densityOption}
          setDensityOption={setDensityOption}
          grainDensity={grainDensity}
          setGrainDensity={setGrainDensity}
          error={densityError}
          classes={classes}
        />
        <Grid item xs={10}>
          <Divider className={classes.divider} />
        </Grid>
      </Grid>
    </>
  );
};

type NoPackFactorFieldsProps = {
  densityOption: string;
  setDensityOption: (value: string) => void;
  grainDensity: number;
  setGrainDensity: (value: number) => void;
  volToBuOption: string;
  setVolToBuOption: (value: string) => void;
  buPerCuft: number;
  setBuPerCuft: (value: number) => void;
  coneOption: string;
  setConeOption: (value: string) => void;
  coneAdjHeight: number;
  setConeAdjHeight: (value: number) => void;
  volAdjOption: GrainVolumeAdjustmentOption;
  setVolAdjOption: (value: GrainVolumeAdjustmentOption) => void;
  volAdjValue: number;
  setVolAdjValue: (value: number) => void;
  volAdjType: string;
  setVolAdjType: (value: string) => void;
  coneError?: string;
  volumeError?: string;
  conversionError?: string;
  densityError?: string;
  classes: Record<string, string>;
};

/**
 * NoPackFactorFields renders the configuration form fields when pack factor compensation
 * is not being applied to grain inventory calculations.
 *
 * This component displays four main calculation configuration sections:
 * 1. Volume to Bushel Conversion - Controls how cubic feet are converted to bushels
 * 2. Cone Volume Calculation - Adjusts for grain pile cone shape
 * 3. Grain Volume Offset - Manual volume adjustments (Deductions/Additions)
 * 4. Grain Density Calculation - Determines grain weight per cubic foot
 *
 * This mode is used when the facility prefers simpler volume-based calculations without
 * accounting for grain compaction effects that pack factors would normally address.
 *
 * **GraphQL Model Fields Updated (InventoryCalculationParameters):**
 *
 * When NO_PACK_FACTOR is selected, the following fields are expected to be set/updated:
 * - `pack_factor_calculation_option` - Set to 'NO_PACK_FACTOR'
 * - `volume_to_bu_conversion_option` - Either 'standard' or 'custom' based on volToBuOption
 * - `bu_per_cuft_conversion_factor` - Custom bu/cu.ft. value when volToBuOption is 'custom'
 * - `grain_volume_calculation_option` - Either 'standard' or 'custom' based on coneOption
 * - `equivalent_height_cone_adjustment` - Height adjustment in feet when coneOption is 'custom'
 * - `grain_volume_adjustment_option` - One of 'None', 'Deduction', 'Addition' based on volAdjOption
 * - `grain_volume_adjustment_cuft` - Volume adjustment in cu.ft. when volAdjOption is not 'None'
 * - `grain_density_calculation_option` - Either 'standard' or 'custom' based on densityOption
 * - `grain_density_lbspercuft` - Custom density in lbs/cu.ft. when densityOption is 'custom'
 *
 * @param props - Component properties including all calculation option states and setters
 * @param props.densityOption - Grain density option: 'standard' or 'custom'
 * @param props.setDensityOption - Setter for density option
 * @param props.grainDensity - Custom grain density in lbs/cu.ft.
 * @param props.setGrainDensity - Setter for grain density value
 * @param props.volToBuOption - Volume conversion option: 'standard' or 'custom'
 * @param props.setVolToBuOption - Setter for volume conversion option
 * @param props.buPerCuft - Bushels per cubic foot conversion factor
 * @param props.setBuPerCuft - Setter for bu/cu.ft. conversion factor
 * @param props.coneOption - Cone calculation option: 'standard' or 'custom'
 * @param props.setConeOption - Setter for cone calculation option
 * @param props.coneAdjHeight - Cone adjustment height in feet
 * @param props.setConeAdjHeight - Setter for cone adjustment height
 * @param props.volAdjOption - Volume adjustment option: 'standard' or 'custom'
 * @param props.setVolAdjOption - Setter for volume adjustment option
 * @param props.volAdjValue - Volume adjustment value in cu.ft.
 * @param props.setVolAdjValue - Setter for volume adjustment value
 * @param props.volAdjType - Volume adjustment type: 'none', 'deduction', or 'addition'
 * @param props.setVolAdjType - Setter for volume adjustment type
 * @param props.classes - Material-UI style classes object
 */
const NoPackFactorFields: React.FC<NoPackFactorFieldsProps> = ({
  densityOption,
  setDensityOption,
  grainDensity,
  setGrainDensity,
  volToBuOption,
  setVolToBuOption,
  buPerCuft,
  setBuPerCuft,
  coneOption,
  setConeOption,
  coneAdjHeight,
  setConeAdjHeight,
  volAdjOption,
  setVolAdjOption,
  volAdjValue,
  setVolAdjValue,
  volAdjType,
  setVolAdjType,
  coneError,
  volumeError,
  conversionError,
  densityError,
  classes,
}) => {
  return (
    <>
      <Grid item xs={10}>
        <Divider className={classes.divider} />
      </Grid>
      {/* Volume to Bu. Section */}
      <Typography variant="subtitle2" color="textSecondary" className={classes.sectionHeader}>
        Volume to Bu. Conversion
      </Typography>
      <Grid container spacing={2} className={classes.section} justify="center">
        <VolumeConversionFields
          volToBuOption={volToBuOption}
          setVolToBuOption={setVolToBuOption}
          buPerCuft={buPerCuft}
          setBuPerCuft={setBuPerCuft}
          error={conversionError}
          classes={classes}
        />
        <Grid item xs={10}>
          <Divider className={classes.divider} />
        </Grid>
      </Grid>

      {/* Cone Volume Section */}
      <Typography variant="subtitle2" color="textSecondary" className={classes.sectionHeader}>
        Cone Volume Calculation
      </Typography>
      <Grid container spacing={2} className={classes.section} justify="center">
        <ConeVolumeFields
          coneOption={coneOption}
          setConeOption={setConeOption}
          coneAdjHeight={coneAdjHeight}
          setConeAdjHeight={setConeAdjHeight}
          error={coneError}
          classes={classes}
        />
        <Grid item xs={10}>
          <Divider className={classes.divider} />
        </Grid>
      </Grid>

      {/* Grain Volume Offset Section */}
      <Typography variant="subtitle2" color="textSecondary" className={classes.sectionHeader}>
        Grain Volume Offset
      </Typography>
      <Grid container spacing={2} className={classes.section} justify="center">
        <VolumeAdjustmentFields
          volAdjOption={volAdjOption}
          setVolAdjOption={setVolAdjOption}
          volAdjValue={volAdjValue}
          setVolAdjValue={setVolAdjValue}
          volAdjType={volAdjType}
          setVolAdjType={setVolAdjType}
          error={volumeError}
          classes={classes}
        />
        <Grid item xs={10}>
          <Divider className={classes.divider} />
        </Grid>
      </Grid>

      {/* Grain Density Section */}
      <Typography variant="subtitle2" color="textSecondary" className={classes.sectionHeader}>
        Grain Density Calculation
      </Typography>
      <Grid container spacing={2} className={classes.section} justify="center">
        <GrainDensityFields
          densityOption={densityOption}
          setDensityOption={setDensityOption}
          grainDensity={grainDensity}
          setGrainDensity={setGrainDensity}
          error={densityError}
          classes={classes}
        />
        <Grid item xs={10}>
          <Divider className={classes.divider} />
        </Grid>
      </Grid>
    </>
  );
};

type PackFactorConfigurationFieldsProps = {
  packFactorOption: PackFactorCalculationOption;
  pfTestWeight: number;
  setPfTestWeight: (value: number) => void;
  pfAdjIndex: number;
  setPfAdjIndex: (value: number) => void;
  pfValue: number;
  setPfValue: (value: number) => void;
  densityOption: string;
  setDensityOption: (value: string) => void;
  grainDensity: number;
  setGrainDensity: (value: number) => void;
  volToBuOption: string;
  setVolToBuOption: (value: string) => void;
  buPerCuft: number;
  setBuPerCuft: (value: number) => void;
  coneOption: string;
  setConeOption: (value: string) => void;
  coneAdjHeight: number;
  setConeAdjHeight: (value: number) => void;
  volAdjOption: GrainVolumeAdjustmentOption;
  setVolAdjOption: (value: GrainVolumeAdjustmentOption) => void;
  volAdjValue: number;
  setVolAdjValue: (value: number) => void;
  volAdjType: string;
  setVolAdjType: (value: string) => void;
  coneError?: string;
  volumeError?: string;
  conversionError?: string;
  densityError?: string;
  classes: Record<string, string>;
};

/**
 * PackFactorConfigurationFields is a dynamic form component that renders different sets of
 * input fields based on the selected pack factor calculation option for grain bin inventory.
 *
 * This component is central to inventory calculations as it allows users to configure how
 * grain volume and weight are calculated in storage bins. It supports multiple calculation
 * methods including
 * <br>
 * - No pack factor compensation (basic volume calculations)
 * - USDA pack factor tables (industry standard for grain compaction)
 * - Custom pack factor tables and values (facility-specific adjustments)
 *
 * The pack factor accounts for grain compaction in storage, which affects the accuracy of
 * inventory weight calculations. Additional settings like volume adjustments, cone volume
 * calculations, and grain density are conditionally displayed based on the calculation method
 * selected, ensuring users only see relevant configuration options for their chosen approach.
 */
const PackFactorConfigurationFields: React.FC<PackFactorConfigurationFieldsProps> = ({
  packFactorOption,
  pfTestWeight,
  setPfTestWeight,
  pfAdjIndex,
  setPfAdjIndex,
  pfValue,
  setPfValue,
  densityOption,
  setDensityOption,
  grainDensity,
  setGrainDensity,
  volToBuOption,
  setVolToBuOption,
  buPerCuft,
  setBuPerCuft,
  coneOption,
  setConeOption,
  coneAdjHeight,
  setConeAdjHeight,
  volAdjOption,
  setVolAdjOption,
  volAdjValue,
  setVolAdjValue,
  volAdjType,
  setVolAdjType,
  coneError,
  volumeError,
  conversionError,
  densityError,
  classes,
}) => {
  switch (packFactorOption) {
    case PackFactorCalculationOption.CUSTOM_PACK_FACTOR_SINGLE:
      return (
        <CustomPackFactorSingleFields
          pfValue={pfValue}
          setPfValue={setPfValue}
          densityOption={densityOption}
          setDensityOption={setDensityOption}
          grainDensity={grainDensity}
          setGrainDensity={setGrainDensity}
          volToBuOption={volToBuOption}
          setVolToBuOption={setVolToBuOption}
          buPerCuft={buPerCuft}
          setBuPerCuft={setBuPerCuft}
          coneOption={coneOption}
          setConeOption={setConeOption}
          coneAdjHeight={coneAdjHeight}
          setConeAdjHeight={setConeAdjHeight}
          volAdjOption={volAdjOption}
          setVolAdjOption={setVolAdjOption}
          volAdjValue={volAdjValue}
          setVolAdjValue={setVolAdjValue}
          volAdjType={volAdjType}
          setVolAdjType={setVolAdjType}
          coneError={coneError}
          volumeError={volumeError}
          conversionError={conversionError}
          densityError={densityError}
          classes={classes}
        />
      );
    case PackFactorCalculationOption.NO_PACK_FACTOR:
      return (
        <NoPackFactorFields
          densityOption={densityOption}
          setDensityOption={setDensityOption}
          grainDensity={grainDensity}
          setGrainDensity={setGrainDensity}
          volToBuOption={volToBuOption}
          setVolToBuOption={setVolToBuOption}
          buPerCuft={buPerCuft}
          setBuPerCuft={setBuPerCuft}
          coneOption={coneOption}
          setConeOption={setConeOption}
          coneAdjHeight={coneAdjHeight}
          setConeAdjHeight={setConeAdjHeight}
          volAdjOption={volAdjOption}
          setVolAdjOption={setVolAdjOption}
          volAdjValue={volAdjValue}
          setVolAdjValue={setVolAdjValue}
          volAdjType={volAdjType}
          setVolAdjType={setVolAdjType}
          coneError={coneError}
          volumeError={volumeError}
          conversionError={conversionError}
          densityError={densityError}
          classes={classes}
        />
      );
    case PackFactorCalculationOption.USDA_PACK_FACTOR:
      return (
        <UsdaPackFactorFields
          pfTestWeight={pfTestWeight}
          setPfTestWeight={setPfTestWeight}
          pfValue={pfValue}
          coneOption={coneOption}
          setConeOption={setConeOption}
          coneAdjHeight={coneAdjHeight}
          setConeAdjHeight={setConeAdjHeight}
          volAdjOption={volAdjOption}
          setVolAdjOption={setVolAdjOption}
          volAdjValue={volAdjValue}
          setVolAdjValue={setVolAdjValue}
          volAdjType={volAdjType}
          setVolAdjType={setVolAdjType}
          densityOption={densityOption}
          setDensityOption={setDensityOption}
          grainDensity={grainDensity}
          setGrainDensity={setGrainDensity}
          coneError={coneError}
          volumeError={volumeError}
          densityError={densityError}
          classes={classes}
        />
      );
    case PackFactorCalculationOption.CUSTOM_PACK_FACTOR_TABLE:
      return (
        <CustomPackFactorTableFields
          pfTestWeight={pfTestWeight}
          setPfTestWeight={setPfTestWeight}
          pfValue={pfValue}
          classes={classes}
        />
      );
    case PackFactorCalculationOption.CUSTOM_PACK_FACTOR_WITH_ADJUSTMENT_INDEX:
      return (
        <CustomPackFactorWithAdjustmentFields
          pfTestWeight={pfTestWeight}
          setPfTestWeight={setPfTestWeight}
          pfAdjIndex={pfAdjIndex}
          setPfAdjIndex={setPfAdjIndex}
          pfValue={pfValue}
          setPfValue={setPfValue}
        />
      );

    default:
      return null;
  }
};

export type InventoryCalculationSettingsDialogOwnProps = {
  open: boolean;
  grain_bin_id: number;
  onClose: () => void;
  onSaved?: () => void;
};

export enum PackFactorCalculationOption {
  NO_PACK_FACTOR = 'NO_PACK_FACTOR',
  CUSTOM_PACK_FACTOR_SINGLE = 'CUSTOM_PACK_FACTOR_SINGLE',
  CUSTOM_PACK_FACTOR_WITH_ADJUSTMENT_INDEX = 'CUSTOM_PACK_FACTOR_WITH_ADJUSTMENT_INDEX',
  USDA_PACK_FACTOR = 'USDA_PACK_FACTOR',
  CUSTOM_PACK_FACTOR_TABLE = 'CUSTOM_PACK_FACTOR_TABLE',
}

export enum GrainVolumeAdjustmentOption {
  NONE = 'None',
  DEDUCTION = 'Deduction',
  ADDITION = 'Addition',
}

const convertToPackFactorCalculationOption = (pack_factor_calculation_option: string) => {
  switch (pack_factor_calculation_option) {
    case PackFactorCalculationOption.CUSTOM_PACK_FACTOR_WITH_ADJUSTMENT_INDEX:
      return PackFactorCalculationOption.CUSTOM_PACK_FACTOR_WITH_ADJUSTMENT_INDEX;

    case PackFactorCalculationOption.USDA_PACK_FACTOR:
      return PackFactorCalculationOption.USDA_PACK_FACTOR;

    case PackFactorCalculationOption.CUSTOM_PACK_FACTOR_TABLE:
      return PackFactorCalculationOption.CUSTOM_PACK_FACTOR_TABLE;

    case PackFactorCalculationOption.CUSTOM_PACK_FACTOR_SINGLE:
      return PackFactorCalculationOption.CUSTOM_PACK_FACTOR_SINGLE;

    case PackFactorCalculationOption.NO_PACK_FACTOR:
    default:
      return PackFactorCalculationOption.NO_PACK_FACTOR;
  }
};

type Props = InventoryCalculationSettingsDialogOwnProps &
  WithInventoryCalculationParametersHocChildProps &
  WithUpdateInventoryCalculationParametersHocChildProps;

const InventoryCalculationSettingsDialog: React.FC<Props> = ({
  open,
  onClose,
  onSaved,
  grain_bin_id,
  inventory_calculation_parameters,
  refetchInventoryCalculationParameters,
  updateInventoryCalculationParameters,
  loading,
}) => {
  const [packFactorOption, setPackFactorOption] = useState<PackFactorCalculationOption>(
    PackFactorCalculationOption.NO_PACK_FACTOR
  );
  const [pfTestWeight, setPfTestWeight] = useState<number>(0);
  const [pfAdjIndex, setPfAdjIndex] = useState<number>(0);
  const [pfValue, setPfValue] = useState<number>(0);
  const [volToBuOption, setVolToBuOption] = useState<string>('standard');
  const [buPerCuft, setBuPerCuft] = useState<number>(0.80356395);
  const [coneOption, setConeOption] = useState<string>('standard');
  const [coneAdjHeight, setConeAdjHeight] = useState<number>(0);
  const [volAdjOption, setVolAdjOption] = useState<GrainVolumeAdjustmentOption>(
    GrainVolumeAdjustmentOption.NONE
  );
  const [volAdjValue, setVolAdjValue] = useState<number>(0);
  const [volAdjType, setVolAdjType] = useState<string>('none');
  const [densityOption, setDensityOption] = useState<string>('standard');
  const [grainDensity, setGrainDensity] = useState<number>(0);
  const [saving, setSaving] = useState<boolean>(false);
  const classes = useStyles();

  const coneError =
    coneOption === 'custom' && coneAdjHeight === 0
      ? 'Apply a custom cone adjustment height'
      : undefined;

  const volumeAdjustmentError =
    volAdjOption !== GrainVolumeAdjustmentOption.NONE && volAdjValue === 0
      ? 'Apply a custom volume offset'
      : undefined;

  const conversionError =
    volToBuOption === 'custom' && buPerCuft === 0 ? 'Apply a custom conversion factor' : undefined;

  const densityError =
    densityOption === 'custom' && grainDensity === 0 ? 'Apply a custom grain density' : undefined;

  useEffect(() => {
    if (inventory_calculation_parameters) {
      setPackFactorOption(
        convertToPackFactorCalculationOption(
          inventory_calculation_parameters.pack_factor_calculation_option ||
            PackFactorCalculationOption.NO_PACK_FACTOR
        )
      );
      setPfTestWeight(inventory_calculation_parameters.pack_factor_test_weight_lbsperbu || 0);
      setPfAdjIndex(inventory_calculation_parameters.pack_factor_adjustment_index || 0.0);
      setPfValue(inventory_calculation_parameters.pack_factor_value || 0);
      setVolToBuOption(
        inventory_calculation_parameters.volume_to_bu_conversion_option || 'standard'
      );
      setBuPerCuft(inventory_calculation_parameters.bu_per_cuft_conversion_factor || 0.80356395);
      setConeOption(inventory_calculation_parameters.grain_volume_calculation_option || 'standard');
      const adjHeight = inventory_calculation_parameters.equivalent_height_cone_adjustment || 0;
      setConeAdjHeight(adjHeight);

      // Initialize Grain Volume Offset from its dedicated backend fields
      const dbVolAdjOption =
        (inventory_calculation_parameters.grain_volume_adjustment_option as GrainVolumeAdjustmentOption) ||
        GrainVolumeAdjustmentOption.NONE;
      const dbVolAdjValue = inventory_calculation_parameters.grain_volume_adjustment_cuft || 0;

      setVolAdjOption(dbVolAdjOption);
      setVolAdjValue(dbVolAdjValue);

      if (dbVolAdjOption === GrainVolumeAdjustmentOption.NONE) {
        setVolAdjType('none');
      } else {
        if (dbVolAdjValue < 0) {
          setVolAdjType('deduction');
        } else {
          setVolAdjType('addition');
        }
      }

      setDensityOption(
        inventory_calculation_parameters.grain_density_calculation_option || 'standard'
      );
      setGrainDensity(inventory_calculation_parameters.grain_density_lbspercuft || 0);
    }
  }, [inventory_calculation_parameters]);

  const handleSave = async () => {
    try {
      setSaving(true);

      await updateInventoryCalculationParameters({
        grain_bin_id,
        pack_factor_calculation_option: packFactorOption,
        volume_to_bu_conversion_option: volToBuOption,
        grain_volume_calculation_option: coneOption,
        grain_density_calculation_option: densityOption,
        pack_factor_test_weight_lbsperbu: pfTestWeight || 0,
        pack_factor_adjustment_index: pfAdjIndex || 0.0,
        pack_factor_value: pfValue || 0,
        bu_per_cuft_conversion_factor: buPerCuft || 0.80356395,
        equivalent_height_cone_adjustment: coneAdjHeight || 0,
        // Custom grain density in lbs/cu.ft. - only used when grain_density_calculation_option is 'custom'
        grain_density_lbspercuft: grainDensity || 0,
        grain_volume_adjustment_option: volAdjOption,
        grain_volume_adjustment_cuft: volAdjValue || 0,
      });
      await refetchInventoryCalculationParameters();
      if (onSaved) onSaved();
      onClose();
    } catch (e) {
      // no-op; follow existing pattern of silent error for now
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <DialogSpinner open title="Loading..." />;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle className={classes.dialogTitle}>Inventory Calculation Settings</DialogTitle>
      <DialogContent>
        {/* Pack Factor Section */}
        <Typography variant="subtitle2" color="textSecondary" className={classes.sectionHeader}>
          Pack Factor Calculation
        </Typography>
        <Grid container spacing={2} className={classes.section} justify="center">
          <Grid item xs={10}>
            <FormControl variant="outlined" margin="dense" className={classes.formControl}>
              <InputLabel>Pack Factor Calculation Option</InputLabel>
              <Select
                value={packFactorOption}
                onChange={(e: any) => setPackFactorOption(e.target.value)}
                label="Pack Factor Calculation Option"
              >
                <MenuItem value={PackFactorCalculationOption.NO_PACK_FACTOR}>
                  No Pack Factor Compensation
                </MenuItem>
                {/*<MenuItem value={PackFactorCalculationOption.USDA_PACK_FACTOR}>*/}
                {/*  USDA Pack Factor Table*/}
                {/*</MenuItem>*/}
                {/*<MenuItem value={PackFactorCalculationOption.CUSTOM_PACK_FACTOR_TABLE}>*/}
                {/*  Custom Pack Factor Table*/}
                {/*</MenuItem>*/}
                {/*<MenuItem*/}
                {/*  value={PackFactorCalculationOption.CUSTOM_PACK_FACTOR_WITH_ADJUSTMENT_INDEX}*/}
                {/*>*/}
                {/*  Custom Pack Factor Table with Adjustment Index*/}
                {/*</MenuItem>*/}
                <MenuItem value={PackFactorCalculationOption.CUSTOM_PACK_FACTOR_SINGLE}>
                  Custom Pack Factor Value
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <PackFactorConfigurationFields
            packFactorOption={packFactorOption}
            pfTestWeight={pfTestWeight}
            setPfTestWeight={setPfTestWeight}
            pfAdjIndex={pfAdjIndex}
            setPfAdjIndex={setPfAdjIndex}
            pfValue={pfValue}
            setPfValue={setPfValue}
            densityOption={densityOption}
            setDensityOption={setDensityOption}
            grainDensity={grainDensity}
            setGrainDensity={setGrainDensity}
            volToBuOption={volToBuOption}
            setVolToBuOption={setVolToBuOption}
            buPerCuft={buPerCuft}
            setBuPerCuft={setBuPerCuft}
            coneOption={coneOption}
            setConeOption={setConeOption}
            coneAdjHeight={coneAdjHeight}
            setConeAdjHeight={setConeAdjHeight}
            volAdjOption={volAdjOption}
            setVolAdjOption={setVolAdjOption}
            volAdjValue={volAdjValue}
            setVolAdjValue={setVolAdjValue}
            volAdjType={volAdjType}
            setVolAdjType={setVolAdjType}
            coneError={coneError}
            volumeError={volumeAdjustmentError}
            conversionError={conversionError}
            densityError={densityError}
            classes={classes}
          />
        </Grid>
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <Button onClick={onClose} disabled={saving} className={classes.cancelButton}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={
            saving || !!coneError || !!volumeAdjustmentError || !!conversionError || !!densityError
          }
          className={classes.saveButton}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default withInventoryCalculationParametersHoc(
  withUpdateInventoryCalculationParametersHoc(InventoryCalculationSettingsDialog)
);
